import {ok} from "neverthrow"
import {useAsyncResult} from "./useAsyncResult"
import {useAccessToken} from "../services/auth"
import {AuditLog, AuditLogResource, findAuditLogs} from "../resources/auditLog"
import {PaymentType} from "../resources/payment"
import {hasPermission} from "../services/permission"

export enum PaymentReviewAction {
	Approve = "Approve",
	Reject = "Reject",
}

const REVIEW_INTENTS = Object.values(PaymentReviewAction)

export interface PaymentReviewInfo {
	action: PaymentReviewAction
	actorName?: string
	actorRole: string
	actorType: string
	timestamp: string
}

export function getPaymentAuditLogResourceTypes(paymentType: PaymentType): AuditLogResource[] {
	switch (paymentType) {
		case PaymentType.ACHPayment:
			return ["AchPayment"]
		case PaymentType.WirePayment:
			return ["WirePayment", "FedWirePayment"]
		default:
			return []
	}
}

function extractReviewInfo(auditLogs: AuditLog[]): PaymentReviewInfo | undefined {
	const reviewEntry = auditLogs.find((log) => REVIEW_INTENTS.includes(log.attributes.intent as PaymentReviewAction))
	if (!reviewEntry) return undefined

	const {intent, actorUserName, actorUserRole, actorUserType, actionTimestamp} = reviewEntry.attributes
	return {
		action: intent as PaymentReviewAction,
		actorName: actorUserName,
		actorRole: actorUserRole,
		actorType: actorUserType,
		timestamp: actionTimestamp,
	}
}

export function usePaymentReviewInfo(paymentId: string, paymentType: PaymentType): PaymentReviewInfo | undefined {
	const accessToken = useAccessToken()
	const canAccessAuditLog = hasPermission("audit-log", "get")
	const resourceTypes = getPaymentAuditLogResourceTypes(paymentType)

	const auditLogResult = useAsyncResult(() => {
		if (!canAccessAuditLog || resourceTypes.length === 0) {
			return Promise.resolve(ok({data: [] as AuditLog[]}))
		}
		return findAuditLogs(accessToken, 0, 50, [paymentId], resourceTypes)
	}, [paymentId, paymentType, canAccessAuditLog])

	return auditLogResult.match(
		() => undefined,
		(result) => extractReviewInfo(result.data),
		() => undefined
	)
}
